import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  memo,
} from "react";
import { FuelCode } from "../types/codeType";
import styles from "./CodesList.module.css";
import config from "../config";
import * as XLSX from "xlsx";
import ExcelIcon from "../components/icons/ExcelIcon";
import SortIcon from "../components/icons/SortIcon";
import BulkDeleteModel from "../components/codesList/BulkDeleteModel";
import DetailsModel from "../components/codesList/DetailsModel";
import DeleteCodeModal from "../components/codesList/DeleteCodeModal";
import ResetCodeModal from "../components/codesList/ResetCodeModal";
import StatusChangeModal from "../components/codesList/StatusChangeModal";
import AddCodesModal from "../components/codesList/AddCodesModal";
import { useCodes } from "../contexts/CodesContext";

import Switch from "@mui/material/Switch";
import NotUsedCodesSummery from "../components/codesList/NotUsedCodesSummery";
import UsedCodesSummery from "../components/codesList/UsedCodesSummery";
import AllCodesSummery from "../components/codesList/AllCodesSummery";
import SummaryModal from "../modals/SummaryModal";
import { useUser } from "../contexts/UserContext";
import Menu3Dots from "../components/icons/Menu3Dots";
import Button from "../components/UI/Button";
import Input from "../components/UiElements/Input";
import DSButton from "../components/UiElements/Button";
import { AnimatePresence, motion } from "framer-motion";

import useIsRental from "../hooks/useIsRental";
import useIsAdmin from "../hooks/useIsAdmin";
import useIsSuperAdmin from "../hooks/useIsSuperAdmin";

const CodesList: React.FC = () => {
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { codes, setCodes } = useCodes();
  const { loading, setLoading, loadingMore, setLoadingMore } = useUser();
  const [reloadCodes, setReloadCodes] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [codeSearch, setCodeSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<FuelCode | null>(null);
  const [sortStatus, setSortStatus] = useState<"all" | "used" | "available">(
    "all"
  );
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalCode, setStatusModalCode] = useState<FuelCode | null>(null);
  const [showAddCodesModal, setShowAddCodesModal] = useState(false);
  const [addCodesMode, setAddCodesMode] = useState<"excel" | "manual">("excel");
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [manualCodesList, setManualCodesList] = useState<
    { code: string; codeValue: string }[]
  >([{ code: "", codeValue: "" }]);
  const [addCodesLoading, setAddCodesLoading] = useState(false);
  const [addCodesResult, setAddCodesResult] = useState<{
    added: string[];
    alreadyExists: string[];
    error?: string;
  } | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("sentDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [excelGroup, setExcelGroup] = useState<"all" | "used" | "not-used">(
    "all"
  );
  const [updatingLocalSystem, setUpdatingLocalSystem] = useState<string | null>(
    null
  );
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<string[]>([]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [visibleRowsCount, setVisibleRowsCount] = useState(300);

  const isSuperAdmin = useIsSuperAdmin();
  const isAdmin = useIsAdmin() && !isSuperAdmin;
  const isRental = useIsRental() && !isSuperAdmin && !isAdmin;

  // Fetch codes from backend
  const getCodes = async (page = 1, reset = false, searchQuery?: string) => {
    if (page === 1 || reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }
    try {
      let codesList: FuelCode[] = [];
      // If searching, get all data and filter on frontend
      if (searchQuery) {
        // For search, fetch all data using pagination
        let allCodes: FuelCode[] = [];
        let searchPage = 1;
        let hasMoreData = true;

        while (hasMoreData) {
          try {
            let url = `${config.backendUrl}/fule-codes/get-list-of-codes`;
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
              },
              body: JSON.stringify({ page: searchPage, limit: 2000 }),
            });
            const data = await res.json();
            const codes = data.codes || [];
            allCodes = [...allCodes, ...codes];
            hasMoreData =
              data.hasMore !== undefined ? data.hasMore : codes.length === 2000;
            searchPage++;
          } catch (e) {
            hasMoreData = false;
          }
        }

        // Apply universal search filter
        const query = searchQuery.toLowerCase();
        codesList = allCodes.filter((c) => {
          // Search across multiple fields
          const code = c.code?.toLowerCase() || "";
          const sentToPhone = c.sentToUserPhone?.toLowerCase() || "";
          const cardNumber = c.cardNumber?.toLowerCase() || "";
          const userPhone =
            c.userConversationData?.userPhone?.toLowerCase() || "";
          const carNumber =
            c.userConversationData?.carNumberInRafael?.toLowerCase() || "";
          const alternateCarNumber =
            c.userConversationData?.alternateCarNumber?.toLowerCase() || "";
          const fullName =
            c.userConversationData?.fullName?.toLowerCase() || "";
          const idNumber =
            c.userConversationData?.idNumber?.toLowerCase() || "";

          return (
            code.includes(query) ||
            sentToPhone.includes(query) ||
            cardNumber.includes(query) ||
            userPhone.includes(query) ||
            carNumber.includes(query) ||
            alternateCarNumber.includes(query) ||
            fullName.includes(query) ||
            idNumber.includes(query)
          );
        });

        setCodes(codesList);
        setHasMore(false); // No pagination for search
      } else {
        // Default: paginated fetch
        let url = `${config.backendUrl}/fule-codes/get-list-of-codes`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ page, limit: 2000 }),
        });
        const data = await res.json();
        codesList = data.codes || [];
        if (page === 1 || reset) {
          setCodes(codesList);
        } else {
          setCodes((prev) => [...prev, ...codesList]);
        }
        setHasMore(
          data.hasMore !== undefined ? data.hasMore : codesList.length === 2000
        );
      }
      setCurrentPage(page);
    } catch (error) {
      setCodes([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    getCodes(1, true);
  }, []);

  // Handle reloads when reloadCodes changes (but not on initial mount)
  useEffect(() => {
    if (reloadCodes > 0) {
      // Only reload if it's been triggered (counter > 0)
      // Clear search when reloading
      setCodeSearch("");
      setPhoneSearch("");
      setSearchActive(false);
      setCurrentPage(1);
      getCodes(1, true);
    }
  }, [reloadCodes]);

  useEffect(() => {
    setCurrentPage(1);
    // Debounced live search - combine both search terms
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      const combinedQuery = `${codeSearch.trim()} ${phoneSearch.trim()}`.trim();
      if (combinedQuery) {
        // Perform search
        getCodes(1, true, combinedQuery);
        setSearchActive(true);
      } else {
        // Clear search - reload original data
        setSearchActive(false);
        setHasMore(true);
        getCodes(1, true);
      }
    }, 700);
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [codeSearch, phoneSearch]);

  const handleClearSearch = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
    setCodeSearch("");
    setPhoneSearch("");
    setSearchActive(false);
    // Reload original data when clearing search
    setCurrentPage(1);
    setHasMore(true);
    getCodes(1, true);
  };

  const handleToggleActionsMenu = () => {
    setActionsMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!actionsMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target as Node)
      ) {
        setActionsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionsMenuOpen]);

  const filteredRowsCount = useMemo(() => {
    if (sortStatus === "used") return codes.filter((code) => code.used).length;
    if (sortStatus === "available")
      return codes.filter((code) => !code.used).length;
    return codes.length;
  }, [codes, sortStatus]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      const combinedQuery = `${codeSearch.trim()} ${phoneSearch.trim()}`.trim();
      getCodes(nextPage, false, combinedQuery || undefined);
    }
  }, [currentPage, loadingMore, hasMore, codeSearch, phoneSearch]);

  const handleScroll = useCallback(() => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      // Load more when user has scrolled to within 100px of the bottom
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMore();
        if (visibleRowsCount < filteredRowsCount) {
          const newVisibleCount = Math.min(
            visibleRowsCount + 300,
            filteredRowsCount
          );
          setVisibleRowsCount(newVisibleCount);
        }
      }
    }
  }, [loadMore, visibleRowsCount, filteredRowsCount]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, codes]);

  // Focus the input after codes update if search is active
  useEffect(() => {
    if (searchActive) {
      if (codeSearch && searchInputRef.current) {
        searchInputRef.current.focus();
      } else if (phoneSearch && phoneInputRef.current) {
        phoneInputRef.current.focus();
      }
    }
  }, [codes, searchActive]);

  const handleDeleteCode = useCallback((code: FuelCode) => {
    setSelectedCode(code);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (selectedCode) {
      await fetch(
        `${config.backendUrl}/fule-codes/delete-code/${selectedCode.code}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      setReloadCodes((r) => r + 1);
      setShowDeleteModal(false);
      setSelectedCode(null);
    }
  };

  const handleDeleteModalCancel = () => {
    setShowDeleteModal(false);
    setSelectedCode(null);
  };

  const handleResetCode = useCallback((code: FuelCode) => {
    setSelectedCode(code);
    setShowResetModal(true);
  }, []);

  const handleConfirmReset = async () => {
    if (selectedCode) {
      const res = await fetch(`${config.backendUrl}/fule-codes/reset-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ code: selectedCode.code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "שגיאה בהחזרת הקוד");
      setReloadCodes((r) => r + 1);
      setShowResetModal(false);
      setSelectedCode(null);
    }
  };

  const handleResetModalCancel = () => {
    setShowResetModal(false);
    setSelectedCode(null);
  };

  // Filter and sort codes by status
  const filteredCodes = useMemo(() => {
    let filtered = codes;
    if (sortStatus === "used") {
      filtered = filtered.filter((code) => code.used);
    } else if (sortStatus === "available") {
      filtered = filtered.filter((code) => !code.used);
    }
    // Sorting logic
    if (sortColumn === "codeValue") {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a.codeValue;
        const bVal = b.codeValue;
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }
        // Compare as strings if not both numbers
        return sortDirection === "asc"
          ? String(aVal).localeCompare(String(bVal), undefined, {
              numeric: true,
            })
          : String(bVal).localeCompare(String(aVal), undefined, {
              numeric: true,
            });
      });
    } else if (sortColumn === "status") {
      filtered = [...filtered].sort((a, b) => {
        // true > false for 'used', so for asc: available (false) first, used (true) last
        if (a.used === b.used) return 0;
        if (sortDirection === "asc") {
          return a.used ? 1 : -1;
        } else {
          return a.used ? -1 : 1;
        }
      });
    } else if (sortColumn === "sentDate") {
      filtered = [...filtered].sort((a, b) => {
        const aDate =
          a.userConversationData && a.userConversationData.receivedCodeDate
            ? new Date(a.userConversationData.receivedCodeDate).getTime()
            : 0;
        const bDate =
          b.userConversationData && b.userConversationData.receivedCodeDate
            ? new Date(b.userConversationData.receivedCodeDate).getTime()
            : 0;
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      });
    }
    return filtered;
  }, [codes, sortStatus, sortColumn, sortDirection]);

  const visibleCodes = useMemo(
    () => filteredCodes.slice(0, visibleRowsCount),
    [filteredCodes, visibleRowsCount]
  );

  useEffect(() => {
    const initialVisibleRows = Math.min(300, filteredCodes.length || 300);
    setVisibleRowsCount(initialVisibleRows);
  }, [filteredCodes.length, sortStatus]);

  // Handle status cell click
  const handleStatusCellClick = useCallback((code: FuelCode) => {
    // If multiple codes are selected and this code is among them, treat as bulk
    if (selectedCodes.length > 1 && selectedCodes.includes(code.code)) {
      setStatusModalCode(null); // null means bulk
      setShowStatusModal(true);
    } else {
      setStatusModalCode(code);
      setShowStatusModal(true);
    }
  }, [selectedCodes]);

  // Confirm status change
  const handleConfirmStatusChange = async () => {
    if (statusModalCode) {
      // Single code logic (existing)
      await fetch(`${config.backendUrl}/fule-codes/change-code-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          code: statusModalCode.code,
          status: !statusModalCode.used,
        }),
      });
      await loadAllCodes();
      setReloadCodes((r) => r + 1);
      setShowStatusModal(false);
      setStatusModalCode(null);
    } else if (selectedCodes.length > 1) {
      // Bulk logic
      // Find the current status of the first selected code in the filtered list
      const filtered = filteredCodes;
      const firstCode = filtered.find((c) => selectedCodes.includes(c.code));
      if (!firstCode) return;
      const newStatus = !firstCode.used;
      await fetch(`${config.backendUrl}/fule-codes/change-many-codes-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          codes: selectedCodes,
          status: newStatus,
        }),
      });
      await loadAllCodes();
      setReloadCodes((r) => r + 1);
      setShowStatusModal(false);
      setStatusModalCode(null);
    }
  };

  const handleStatusModalCancel = () => {
    setShowStatusModal(false);
    setStatusModalCode(null);
  };

  // Helper to parse Excel file and extract codes and values from columns A and B
  const parseExcelFile = async (
    file: File
  ): Promise<{ code: string; codeValue: string }[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
        }) as string[][];
        // Skip header row if present, and map each row to { code, codeValue }
        const codes = rows
          .filter((row) => row[0] && row[1])
          .map((row) => ({
            code: String(row[0]).trim(),
            codeValue: String(row[1]).trim(),
          }));
        resolve(codes);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleAddCodesSubmit = async () => {
    setAddCodesLoading(true);
    setAddCodesResult(null);
    let codesPayload: { code: string; codeValue: string | number }[] = [];
    try {
      if (addCodesMode === "excel") {
        if (!excelFile) throw new Error("לא נבחר קובץ");
        const codes = await parseExcelFile(excelFile);
        codesPayload = codes.map(({ code, codeValue }) => ({
          code,
          codeValue: isNaN(Number(codeValue)) ? codeValue : Number(codeValue),
        }));
      } else {
        // Use manualCodesList for manual mode
        codesPayload = manualCodesList
          .filter((row) => row.code.trim())
          .map((row) => ({
            code: row.code.trim(),
            codeValue:
              row.codeValue.trim() === ""
                ? row.code.trim()
                : isNaN(Number(row.codeValue))
                ? row.codeValue.trim()
                : Number(row.codeValue),
          }));
        if (!codesPayload.length) throw new Error("לא נמצאו מספרים");
      }
      if (!codesPayload.length) throw new Error("לא נמצאו מספרים");
      const res = await fetch(
        `${config.backendUrl}/fule-codes/add-many-codes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ codes: codesPayload }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "שגיאה בהוספת מספרים");
      setAddCodesResult({
        added: data.added || [],
        alreadyExists: data.alreadyExists || [],
      });
      setReloadCodes((r) => r + 1);
    } catch (err: any) {
      setAddCodesResult({ added: [], alreadyExists: [], error: err.message });
    } finally {
      setAddCodesLoading(false);
    }
  };

  // Helper to translate keys to Hebrew titles
  const translateKeyToHebrew = (key: string) => {
    const map: Record<string, string> = {
      userPhone: "טלפון משתמש",
      carNumberInRafael: "מספר רכב רשום",
      fullName: "שם מלא",
      idNumber: "תעודת זהות",
      alternateCarNumber: "מספר רכב חלופי",
      phone: "טלפון",
      name: "שם",
      reason: "סיבה",
      receivedCode: "מספר תדלוק",
      receivedCodeDate: "נשלח בתאריך",
      receivedCodeValue: "ערך",
    };
    return (
      map[key] ||
      key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
    );
  };

  // Helper to format date strings to dd/mm/yyyy HH:MM
  const formatDateTime = useCallback((value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }, []);

  const handleSelectCode = (code: FuelCode, checked: boolean) => {
    setSelectedCodes((prev) => {
      if (checked) return [...prev, code.code];
      return prev.filter((c) => c !== code.code);
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCodes(filteredCodes.map((c) => c.code));
    } else {
      setSelectedCodes([]);
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedCodes.length === 0) return;
    await fetch(`${config.backendUrl}/fule-codes/delete-many-codes`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({ codes: selectedCodes }),
    });
    setReloadCodes((r) => r + 1);
    setShowBulkDeleteModal(false);
    setSelectedCodes([]);
  };

  const handleBulkDeleteModalCancel = () => {
    setShowBulkDeleteModal(false);
  };

  // Helper to load all codes before sorting
  const loadAllCodes = async () => {
    if (!hasMore) return;
    let page = currentPage;
    let allCodes = [...codes];
    let more: boolean = hasMore;
    while (more) {
      page += 1;
      try {
        let url = `${config.backendUrl}/fule-codes/get-list-of-codes`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ page, limit: 2000 }),
        });
        const data = await res.json();
        const codesList = data.codes || [];
        allCodes = [...allCodes, ...codesList];
        more =
          data.hasMore !== undefined ? data.hasMore : codesList.length === 2000;
      } catch (e) {
        more = false;
      }
    }
    setCodes([...allCodes]);
    setHasMore(false);
  };

  const handleSortTabClick = async (next: "all" | "used" | "available") => {
    if (hasMore) {
      setLoading(true);
      await loadAllCodes();
      setLoading(false);
    }
    setSortStatus(next);
  };

  // Download Excel file from backend
  const handleDownloadExcel = async () => {
    // Map sortStatus to backend group param
    let group: "all" | "used" | "not-used" = "all";
    if (sortStatus === "used") group = "used";
    else if (sortStatus === "available") group = "not-used";
    setLoading(true);
    try {
      const res = await fetch(
        `${config.backendUrl}/fule-codes/export-codes-excel/${group}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      if (!res.ok) throw new Error("Failed to download Excel file");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fuel_codes.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setLoading(false);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("שגיאה בהורדת קובץ אקסל");
    }
  };

  // Handler to toggle inLocalSystem
  const handleToggleInLocalSystem = useCallback(async (code: FuelCode) => {
    setUpdatingLocalSystem(code.code);
    try {
      const res = await fetch(
        `${config.backendUrl}/fule-codes/update-in-local-system/${code.code}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ inLocalSystem: !code.inLocalSystem }),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      setCodes((prev) =>
        prev.map((c) =>
          c.code === code.code ? { ...c, inLocalSystem: !c.inLocalSystem } : c
        )
      );
    } catch (e) {
      alert("שגיאה בעדכון ערך מערכת מקומית");
    } finally {
      setUpdatingLocalSystem(null);
    }
  }, [setCodes]);

  const renderedTableRows = useMemo(
    () =>
      (visibleCodes || []).map((code, index) => (
        <tr key={code._id} className={styles.tableRow}>
          <td className={styles.tableCell}>
            <input
              type="checkbox"
              checked={selectedCodes.includes(code.code)}
              onChange={(e) => {
                if (!isSuperAdmin) return;
                const checked = e.target.checked;
                const shiftKey = (e.nativeEvent as MouseEvent).shiftKey;
                let newSelected = [...selectedCodes];
                if (shiftKey && lastSelectedIndex !== null) {
                  const last = lastSelectedIndex;
                  const current = index;
                  const [start, end] = [
                    Math.min(last, current),
                    Math.max(last, current),
                  ];
                  const rangeCodes = visibleCodes
                    .slice(start, end + 1)
                    .map((c) => c.code);
                  if (checked) {
                    newSelected = Array.from(
                      new Set([...newSelected, ...rangeCodes])
                    );
                  } else {
                    newSelected = newSelected.filter(
                      (c) => !rangeCodes.includes(c)
                    );
                  }
                } else {
                  if (checked) {
                    newSelected.push(code.code);
                  } else {
                    newSelected = newSelected.filter((c) => c !== code.code);
                  }
                }
                setSelectedCodes(newSelected);
                setLastSelectedIndex(index);
              }}
              className={isSuperAdmin ? "" : styles.disabled}
            />
          </td>
          <td className={styles.tableCell}>
            {isSuperAdmin ? code.code : "*************"}
          </td>
          <td className={styles.tableCell}>
            {isRental ? "*************" : code.cardNumber || "-"}
          </td>
          <td className={styles.tableCell}>{code.codeValue}</td>
          <td
            className={styles.tableCell}
            style={{
              fontWeight: "bold",
              cursor: "pointer",
            }}
            title="שנה סטטוס"
          >
            <Button
              color="gray"
              size="small"
              onClick={() => handleStatusCellClick(code)}
              className={styles.statusCell}
              style={{
                backgroundColor: "var(--light-gray-color)",
                color: code.used ? "#e53935" : "var(--green-color)",
              }}
            >
              {code.used ? "בשימוש" : "פנוי"}
            </Button>
          </td>
          <td className={styles.tableCell}>
            <Switch
              checked={!!code.inLocalSystem}
              onChange={() => handleToggleInLocalSystem(code)}
              color="primary"
              disabled={!code.used || updatingLocalSystem === code.code}
              inputProps={{ "aria-label": "in local system" }}
            />
          </td>
          <td className={styles.tableCell}>{code.sentToUserPhone || "-"}</td>
          <td className={styles.tableCell}>
            {code.userConversationData && code.userConversationData.receivedCodeDate
              ? formatDateTime(code.userConversationData.receivedCodeDate)
              : "-"}
          </td>
          <td className={styles.tableCell}>
            {code.userConversationData?.carNumberInRafael || "-"}
          </td>
          <td className={styles.tableCell}>
            {code.userConversationData?.alternateCarNumber || "-"}
          </td>
          <td className={styles.tableCell}>
            {code.used ? (
              <Button
                color="green"
                size="small"
                onClick={() => {
                  setDetailsData(code.userConversationData || null);
                  setShowDetailsModal(true);
                }}
              >
                הצג
              </Button>
            ) : (
              "-"
            )}
          </td>
          <td className={styles.tableCell}>{code.createdByName || "-"}</td>
          <td className={styles.tableCell}>
            {code.createdAt &&
            code.createdAt !== null &&
            code.createdAt !== undefined &&
            code.createdAt !== "" &&
            code.createdByName &&
            code.createdByName !== null &&
            code.createdByName !== undefined &&
            code.createdByName !== ""
              ? formatDateTime(code.createdAt)
              : "-"}
          </td>
          <td className={styles.actionCell}>
            <div className={styles.deleteButtonContainer}>
              {code.used ? (
                <Button
                  color="green"
                  size="small"
                  onClick={() => isSuperAdmin && handleResetCode(code)}
                  className={isSuperAdmin ? "" : styles.disabled}
                >
                  החזר
                </Button>
              ) : (
                <Button
                  color="red"
                  size="small"
                  onClick={() => isSuperAdmin && handleDeleteCode(code)}
                  className={isSuperAdmin ? "" : styles.disabled}
                >
                  מחק
                </Button>
              )}
            </div>
          </td>
        </tr>
      )),
    [
      visibleCodes,
      selectedCodes,
      isSuperAdmin,
      lastSelectedIndex,
      isRental,
      updatingLocalSystem,
      handleStatusCellClick,
      handleToggleInLocalSystem,
      formatDateTime,
      handleResetCode,
      handleDeleteCode,
    ]
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        {/* Code Search Input */}
        <div className={styles.actionsContainer}>
          <div
            className={styles.searchContainer}
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="חפש לפי מספר רכב או קוד"
              value={codeSearch}
              onChange={(e) => setCodeSearch(e.target.value)}
              className={styles.searchInput}
              rtl
              size="medium"
              color="outline"
              width="250px"
            />

            <Input
              ref={phoneInputRef}
              type="text"
              placeholder="חפש לפי טלפון"
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              className={styles.searchInput}
              rtl
              size="medium"
              color="outline"
              width={160}
            />
            <span style={{ display: searchActive ? undefined : "none" }}>
              <DSButton
                buttonType="button"
                label="נקה"
                size="medium"
                color="outlineAccent"
                onClick={handleClearSearch}
                width={'100px'}
              />
            </span>
          </div>
          <div className={styles.dropdownMenu} ref={actionsMenuRef}>
            <div
              onClick={handleToggleActionsMenu}
              style={{
                width: "25px",
                height: "25px",
                cursor: "pointer",
              }}
            >
              <Menu3Dots backgroundColor="var(--light-gray-color)" />
            </div>
            <AnimatePresence>
              {actionsMenuOpen && (
                <motion.div
                  className={styles.menuContent}
                  initial={{ opacity: 1, scale: 0.95, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -6 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = `${config.backendUrl}/download/sample-fuel-codes`;
                      link.download = "sample_fuel_codes.xlsx";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setActionsMenuOpen(false);
                    }}
                  >
                    קובץ לדוגמה
                  </button>
                  <label htmlFor="xslFileMain" className={styles.menuItem}>
                    הוסף מקובץ
                  </label>
                  <input
                    id="xslFileMain"
                    hidden
                    type="file"
                    accept=".xls,.xlsx"
                    disabled={loading || !isSuperAdmin}
                    onChange={async (e) => {
                      if (!isSuperAdmin) return;
                      const file = e.target.files?.[0] || null;
                      if (file) {
                        try {
                          setLoading(true);
                          setActionsMenuOpen(false);
                          // First, upload and parse the file
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append(
                            "userToken",
                            localStorage.getItem("token") || ""
                          );
                          const uploadResponse = await fetch(
                            `${config.backendUrl}/fule-codes/upload-xsl-file`,
                            {
                              method: "POST",
                              headers: {
                                "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                              },
                              body: formData,
                            }
                          );

                          if (!uploadResponse.ok) {
                            const errorData = await uploadResponse.json();
                            throw new Error(
                              errorData.error || "שגיאה בהעלאת הקובץ"
                            );
                          }

                          const uploadResult = await uploadResponse.json();

                          // Always show upload results
                          const { total, successful, failed, updated } =
                            uploadResult.data;
                          const successMessage = `סה"כ: ${total}, הצליח: ${successful}, נכשל: ${failed}${
                            updated ? `, עודכן: ${updated}` : ""
                          }`;

                          if (failed > 0) {
                            console.log(uploadResult.data.errors);
                            // Format errors for display with success summary
                            const formattedErrors = [
                              successMessage,
                              ...uploadResult.data.errors.map(
                                (error: any) =>
                                  `קוד: ${error.code} - ${error.error}`
                              ),
                            ];
                            setUploadSummary(formattedErrors);
                            setShowSummaryModal(true);
                          } else {
                            // Show success message for fully successful uploads
                            setUploadSummary([successMessage]);
                            setShowSummaryModal(true);
                          }

                          setReloadCodes((r) => r + 1);
                        } catch (err) {
                          console.error("Error uploading XSL file:", err);
                          const errorMessage =
                            err instanceof Error ? err.message : "שגיאה לא ידועה";
                          setUploadSummary([`שגיאה בהעלאת הקובץ: ${errorMessage}`]);
                          setShowSummaryModal(true);
                        } finally {
                          setLoading(false);
                          // Reset the input value so the same file can be selected again
                          e.target.value = "";
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={`${styles.menuItem} ${styles.menuItemDelete}`}
                    style={{
                      opacity:
                        selectedCodes.length === 0 || !isSuperAdmin ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (isSuperAdmin) {
                        handleBulkDelete();
                        setActionsMenuOpen(false);
                      }
                    }}
                    disabled={selectedCodes.length === 0 || !isSuperAdmin}
                  >
                    מחק נבחרים
                  </button>
                  <button
                    type="button"
                    className={styles.menuItem}
                    style={{ display: "flex", alignItems: "center", gap: 2 }}
                    onClick={() => {
                      if (isSuperAdmin) {
                        handleDownloadExcel();
                        setActionsMenuOpen(false);
                      }
                    }}
                    title="הורד אקסל"
                    disabled={!isSuperAdmin}
                  >
                    <ExcelIcon size={15} />
                    הורד אקסל
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Sort Buttons */}
        <div className={styles.sortButtonsContainer}>
          <DSButton
            id="all"
            size="small"
            color={sortStatus === "all" ? "secondary" : "outlineSecondary"}
            label="כל המספרים"
            onClick={() => {
              void handleSortTabClick("all");
            }}
          />
          <DSButton
            id="used"
            size="small"
            color={sortStatus === "used" ? "secondary" : "outlineSecondary"}
            label="בשימוש"
            onClick={() => {
              void handleSortTabClick("used");
            }}
          />
          <DSButton
            id="available"
            size="small"
            color={sortStatus === "available" ? "secondary" : "outlineSecondary"}
            label="פנוי"
            onClick={() => {
              void handleSortTabClick("available");
            }}
          />
        </div>
        {/* Show summary for available codes */}
        {sortStatus === "available" && <NotUsedCodesSummery />}
        {sortStatus === "used" && <UsedCodesSummery />}
        {sortStatus === "all" && <AllCodesSummery />}
        {!Array.isArray(codes) || codes.length === 0 ? (
          <p className={styles.noCodesMessage}>
            {!loading && "לא נמצאו קודים"}
          </p>
        ) : (
          <div ref={tableContainerRef} className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th className={styles.tableHeaderCell}>
                    <input
                      type="checkbox"
                      checked={
                        selectedCodes.length === filteredCodes.length &&
                        filteredCodes.length > 0
                      }
                      onChange={(e) =>
                        isSuperAdmin && handleSelectAll(e.target.checked)
                      }
                      className={isSuperAdmin ? "" : styles.disabled}
                    />
                  </th>
                  <th className={styles.tableHeaderCell}>קוד</th>
                  <th className={styles.tableHeaderCell}>מספר כרטיס</th>
                  <th
                    className={styles.tableHeaderCell}
                    onClick={async () => {
                      if (hasMore) {
                        setLoading(true);
                        await loadAllCodes();
                        setLoading(false);
                      }
                      if (sortColumn === "codeValue") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("codeValue");
                        setSortDirection("asc");
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    ערך
                    <SortIcon />
                  </th>
                  <th
                    className={styles.tableHeaderCell}
                    onClick={async () => {
                      if (hasMore) {
                        setLoading(true);
                        await loadAllCodes();
                        setLoading(false);
                      }
                      if (sortColumn === "status") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("status");
                        setSortDirection("asc");
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    סטטוס
                    <SortIcon />
                  </th>
                  <th className={styles.tableHeaderCell}>סטטוס הזנה במערכת</th>
                  <th className={styles.tableHeaderCell}>נשלח ל</th>
                  <th
                    className={styles.tableHeaderCell}
                    onClick={async () => {
                      if (hasMore) {
                        setLoading(true);
                        await loadAllCodes();
                        setLoading(false);
                      }
                      if (sortColumn === "sentDate") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("sentDate");
                        setSortDirection("asc");
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    נשלח בתאריך
                    <SortIcon />
                  </th>
                  <th className={styles.tableHeaderCell}>מספר רכב</th>
                  <th className={styles.tableHeaderCell}>מספר רכב חלופי</th>
                  <th className={styles.tableHeaderCell}>פרטים</th>
                  <th className={styles.tableHeaderCell}>נוצר על ידי</th>
                  <th className={styles.tableHeaderCell}>תאריך יצירה</th>
                  <th className={styles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody>{renderedTableRows}</tbody>
            </table>
            {/* Loading more indicator */}
            {loadingMore && (
              <div className={styles.loadingMoreIndicator}>
                <p className={styles.loadingMoreText}>טוען מספרים נוספים...</p>
              </div>
            )}
            {/* End of data indicator */}
            {!hasMore && codes.length > 0 && (
              <div className={styles.endOfDataIndicator}>
                אין עוד מספרים לטעינה
              </div>
            )}
            {visibleRowsCount < filteredCodes.length && (
              <div className={styles.endOfDataIndicator}>
                מוצגות {visibleRowsCount} מתוך {filteredCodes.length} תוצאות
              </div>
            )}
          </div>
        )}
        {/* Delete Confirmation Modal */}
        <DeleteCodeModal
          show={showDeleteModal}
          selectedCode={selectedCode}
          onCancel={handleDeleteModalCancel}
          onConfirm={handleConfirmDelete}
        />
        {/* Reset Confirmation Modal */}
        <ResetCodeModal
          show={showResetModal}
          selectedCode={selectedCode}
          onCancel={handleResetModalCancel}
          onConfirm={handleConfirmReset}
        />
        {/* Status Change Confirmation Modal */}
        <StatusChangeModal
          show={showStatusModal}
          statusModalCode={statusModalCode}
          isBulk={statusModalCode === null && selectedCodes.length > 1}
          onCancel={handleStatusModalCancel}
          onConfirm={handleConfirmStatusChange}
        />
        <AddCodesModal
          show={showAddCodesModal}
          addCodesMode={addCodesMode}
          setAddCodesMode={setAddCodesMode}
          excelFile={excelFile}
          setExcelFile={setExcelFile}
          manualCodesList={manualCodesList}
          setManualCodesList={setManualCodesList}
          addCodesResult={addCodesResult}
          setAddCodesResult={setAddCodesResult}
          addCodesLoading={addCodesLoading}
          handleAddCodesSubmit={handleAddCodesSubmit}
          setShowAddCodesModal={setShowAddCodesModal}
          parseExcelFile={parseExcelFile}
          ExcelIcon={ExcelIcon}
        />
        {/* Details Modal */}
        <DetailsModel
          showDetailsModal={showDetailsModal}
          detailsData={detailsData}
          setShowDetailsModal={setShowDetailsModal}
          translateKeyToHebrew={translateKeyToHebrew}
          formatDateTime={formatDateTime}
        />
        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <BulkDeleteModel
            selectedCodes={selectedCodes}
            handleBulkDeleteModalCancel={handleBulkDeleteModalCancel}
            handleConfirmBulkDelete={handleConfirmBulkDelete}
          />
        )}

        <SummaryModal
          isOpen={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          title="סיכום הוספת קודים מקובץ"
          errors={uploadSummary}
        />
      </div>
    </div>
  );
};

export default CodesList;
